/* eslint-disable react-hooks/exhaustive-deps */

import React, { useLayoutEffect, useState } from "react";

import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";

import {
  Layout,
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Spin,
  Button,
  Space,
} from "antd";

import {
  RiseOutlined,
  FallOutlined,
  MinusOutlined,
  EyeOutlined,
} from "@ant-design/icons";

import { useNavigate } from "react-router-dom";

import { getAPI } from "../../utils/common";

import { SMART_PRICE, SMART_PRICE_CALENDAR } from "../../constants/api";
import dayjs from "dayjs";

function SmartPriceDashboard() {
  const { Content } = Layout;

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [dashboard, setDashboard] = useState({});

  const [calendar, setCalendar] = useState([]);

  useLayoutEffect(() => {
    getDashboard();

    getCalendar();
  }, []);

  /*
|--------------------------------------------------------------------------
| Dashboard API
|--------------------------------------------------------------------------
*/

  const getDashboard = () => {
    setLoading(true);

    const successFn = (data) => {
      if (data.status === true) {
        setDashboard(data.data);
      }

      setLoading(false);
    };

    const errorFn = () => {
      setLoading(false);
    };

    getAPI(SMART_PRICE + "/dashboard", successFn, errorFn, {});
  };

  /*
|--------------------------------------------------------------------------
| Demand Calendar
|--------------------------------------------------------------------------
*/

  const getCalendar = () => {
    const successFn = (data) => {
      if (data.status === true) {
        setCalendar(data.data.slice(0, 7));
      }
    };

    const errorFn = () => {};

    getAPI(SMART_PRICE_CALENDAR, successFn, errorFn, {});
  };

  const suggestionColumns = [
    {
      title: "Room",
      dataIndex: "room",
    },

    {
      title: "Old Price",
      dataIndex: "old",
    },

    {
      title: "Suggested",
      dataIndex: "new",
    },

    {
      title: "Change",
      dataIndex: "change",
    },

    {
      title: "Direction",
      dataIndex: "direction",
      render: (value) => {
        if (value === "increase") return <Tag color="green">Increase</Tag>;

        if (value === "decrease") return <Tag color="red">Decrease</Tag>;

        return <Tag>Maintain</Tag>;
      },
    },

    {
      title: "Action",
      render: (_, row) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => navigate("/smart-price/room/" + row.id)}
        >
          View
        </Button>
      ),
    },
  ];
  return (
    <Layout>
      <LayoutHeader />

      <Content>
        <Card
          title="Smart Pricing Dashboard"
          bordered={false}
          extra={
            <Button
              onClick={() => {
                getDashboard();
                getCalendar();
              }}
            >
              Refresh
            </Button>
          }
        >
          <Spin spinning={loading}>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Rooms"
                    value={dashboard.total_rooms || 0}
                  />
                </Card>
              </Col>

              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Suggestions"
                    value={dashboard.total_suggestions || 0}
                  />
                </Card>
              </Col>

              <Col span={6}>
                <Card>
                  <Statistic
                    title="Pending Suggestions"
                    value={dashboard.pending || 0}
                    valueStyle={{
                      color: "#faad14",
                    }}
                  />
                </Card>
              </Col>

              <Col span={6}>
                <Card>
                  <Statistic
                    title="Approved"
                    value={dashboard.approved || 0}
                    valueStyle={{
                      color: "#52c41a",
                    }}
                  />
                </Card>
              </Col>

              <Col span={6}>
                <Card>
                  <Statistic
                    title="Rejected"
                    value={dashboard.rejected || 0}
                    valueStyle={{
                      color: "#ff4d4f",
                    }}
                  />
                </Card>
              </Col>

              <Col span={6}>
                <Card>
                  <Statistic
                    title="Average Old Price"
                    value={dashboard.price_summary?.average_old_price || 0}
                    prefix="₹"
                  />
                </Card>
              </Col>

              <Col span={6}>
                <Card>
                  <Statistic
                    title="Average Suggested Price"
                    value={
                      dashboard.price_summary?.average_suggested_price || 0
                    }
                    prefix="₹"
                  />
                </Card>
              </Col>

              <Col span={6}>
                <Card>
                  <Statistic
                    title="Average Change"
                    value={
                      dashboard.price_summary?.average_change_percentage || 0
                    }
                    suffix="%"
                    valueStyle={{
                      color:
                        dashboard.price_summary?.average_change_percentage < 0
                          ? "#ff4d4f"
                          : "#52c41a",
                    }}
                  />
                </Card>
              </Col>
            </Row>

            <br />

            <Card title="Price Direction Summary">
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Increase"
                    value={dashboard.direction?.increase || 0}
                    prefix={<RiseOutlined />}
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>

                <Col span={8}>
                  <Statistic
                    title="Decrease"
                    value={dashboard.direction?.decrease || 0}
                    prefix={<FallOutlined />}
                    valueStyle={{ color: "#ff4d4f" }}
                  />
                </Col>

                <Col span={8}>
                  <Statistic
                    title="Maintain"
                    value={dashboard.direction?.maintain || 0}
                    prefix={<MinusOutlined />}
                  />
                </Col>
              </Row>
            </Card>

            <br />

            <Card title="Last Generated">
              {dashboard.last_generated
                ? dayjs(dashboard.last_generated).format("DD MMM YYYY")
                : "-"}
            </Card>

            <br />

            <Card
              title="Demand Overview"
              extra={
                <Button onClick={() => navigate("/smart-price/calendar")}>
                  View Calendar
                </Button>
              }
            >
              <Row gutter={[16, 16]}>
                {calendar.map((item, index) => (
                  <Col span={3} key={index}>
                    <Card>
                      <center>
                        <b>{dayjs(item.date).format("DD MMM YYYY")}</b>
                        <br />
                        {item.level === "High Demand" ? (
                          <Tag color="red">High</Tag>
                        ) : item.level === "Medium Demand" ? (
                          <Tag color="orange">Medium</Tag>
                        ) : (
                          <Tag color="green">Stable</Tag>
                        )}
                        <br />
                        Score : {item.score}
                      </center>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </Spin>
        </Card>
      </Content>

      <LayoutFooter />
    </Layout>
  );
}

export default SmartPriceDashboard;
